// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for creating new assets in the system.
 * This module provides a form interface for registering assets with
 * their item type, serial number, location, and inventory date.
 * Includes validation, error handling, and integration with external
 * item and location creation workflows.
 *
 * Key features:
 * - Smart item filtering for asset-enabled items
 * - Real-time serial number validation
 * - Integrated location selection
 * - Date validation and normalization
 * - External creation workflows
 * - Optimistic updates with error recovery
 * - Accessibility support
 */
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError, hasUniqueViolationError } from '@/api/error-utils';
import { ExistingAsset } from '@/api/types';
import { createAsset } from '@/asset/api/create-asset';
import { TakenSerialLink } from '@/asset/components/taken-serial-link';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormTextField } from '@/components/form-text-field';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { useToast } from '@/contexts/toast-context';
import { FormItemSearch } from '@/items/components/form-item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { SparseItem } from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Filters items to only show those that support assets.
 * Used to ensure only trackable items (devices) can be selected for assets.
 * This prevents users from creating assets for items that don't
 * support asset tracking (e.g., services or bulk items).
 *
 * @param items - Items to filter
 * @returns Items that support asset tracking
 *
 * @example
 * ```ts
 * const items = [{ id: '123', name: 'Laptop', hasAssets: true }];
 * const filtered = filterItems(items); // Returns items with hasAssets: true
 * ```
 */
function filterItems(items: SparseItem[]) {
  return items.filter((item) => item.hasAssets);
}

/**
 * Schema for validating the serial number.
 * Requires a non-empty string to ensure unique identification.
 * The serial number must be unique across all assets in the system.
 */
const serialSchema = z.string().min(1, 'Serial is required');

/**
 * Schema for validating item selection.
 * Requires both ID and name to ensure complete item information.
 * Used to validate item selection from the search component.
 */
const itemSchema = z.object(
  { id: z.string(), name: z.string() },
  { message: 'Item is required' }
);

/**
 * Schema for validating location selection.
 * Requires both ID and name to ensure complete location information.
 * Used to validate location selection from the search component.
 */
const locationSchema = z.object(
  { id: z.string(), name: z.string() },
  { message: 'Location is required' }
);

/**
 * Schema for validating inventory date.
 * Requires a valid date.
 * Validates that the date is not in the future and not before January 1, 1970.
 */
const inventoryDateSchema = dateSchema
  .nullable()
  .refine((date): date is DateTime => date !== null, {
    message: 'Inventory date is required',
  })
  .refine((date) => date >= MINIMUM_DATE, {
    message: 'Inventory date cannot be before January 1, 1970',
  })
  .refine(
    (date) => date <= DateTime.now().plus({ days: 1 }).endOf('day'), // Add 1 day to allow for timezone differences
    {
      message: 'Inventory date cannot be in the future',
    }
  );

/**
 * Form data structure for user input.
 * Includes nullable fields for item and location to support
 * progressive form completion. This structure represents the
 * raw form data before validation.
 *
 * @example
 * ```ts
 * const formData: InputFormData = {
 *   item: { id: '123', name: 'Laptop' },
 *   serial: 'LAP123',
 *   location: { id: '456', name: 'Warehouse A' },
 *   inventoryDate: DateTime.fromISO('2024-01-01')
 * };
 * ```
 */
interface InputFormData {
  item: z.infer<typeof itemSchema> | null;
  serial: z.infer<typeof serialSchema>;
  location: z.infer<typeof locationSchema> | null;
  inventoryDate: DateTime | null;
}

/**
 * Schema for validating complete form output.
 * Combines all field schemas and ensures required data is present.
 * This schema is used to validate the form data before submission.
 */
const outputSchema = z.object({
  item: itemSchema,
  serial: serialSchema,
  location: locationSchema,
  inventoryDate: inventoryDateSchema,
});

type OutputFormData = z.infer<typeof outputSchema>;

/**
 * Props for configuring the CreateAssetDialog component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * Controls the visibility of the dialog.
   * When true, the dialog is displayed to the user.
   * The dialog can be closed through the close button,
   * clicking outside, or successful asset creation.
   */
  isOpen: boolean;

  /**
   * Callback function for dialog closure.
   * Called when the user cancels or completes asset creation.
   * The parent component should update its state to hide the dialog.
   */
  onClose: () => void;
}

/**
 * A dialog component for creating new assets.
 * Provides a form interface for entering asset details with validation
 * and error handling. Supports creating new items or locations
 * through external workflows when needed items don't exist.
 *
 * Features:
 * - Item selection with asset-enabled filtering
 * - Serial number validation with uniqueness check
 * - Location selection with creation support
 * - Inventory date selection with future date restriction
 * - Form validation with error messages
 * - Optimistic updates with error handling
 * - External workflows for item/location creation
 *
 * Form Fields:
 * - Item: Search and select from asset-enabled items
 * - Serial: Unique identifier for the asset
 * - Location: Search and select from available locations
 * - Inventory Date: When the asset was/will be received
 *
 * Error Handling:
 * - Duplicate serial numbers
 * - Network/API failures
 * - Invalid form data
 * - Missing required fields
 *
 * Usage example:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * return (
 *   <CreateAssetDialog
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *   />
 * );
 * ```
 *
 * @param props - Component configuration options
 */
export function CreateAssetDialog(props: Props) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [takenSerialAsset, setTakenSerialAsset] =
    useState<ExistingAsset | null>(null);

  const createAssetMutation = useMutation({
    mutationFn: createAsset,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['asset'] });
      queryClient.invalidateQueries({
        queryKey: ['time-series', 'asset-location'],
      });

      close();

      showToast({
        severity: 'success',
        element: (
          <Typography>
            Asset{' '}
            <Link
              to="/assets/$assetKey"
              params={{ assetKey: response.asset.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {response.asset.serial}
            </Link>{' '}
            created
          </Typography>
        ),
      });
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      // Check for duplicate serial number
      if (hasUniqueViolationError(error)) {
        setTakenSerialAsset(apiError.existingAsset ?? null);
        setError('serial', {
          type: 'custom',
          message: 'An asset with this serial already exists',
        });
      }

      // Check for invalid inventory date (future date)
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_inventory_date
      ) {
        setError('inventoryDate', {
          type: 'custom',
          message: 'Inventory date cannot be in the future',
        });
      }
    },
  });

  const { control, formState, reset, setError, handleSubmit } = useForm<
    InputFormData,
    unknown,
    OutputFormData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: {
      item: null,
      serial: '',
      location: null,
      inventoryDate: null,
    },
  });

  const isAlertOpen =
    createAssetMutation.isError &&
    !hasUniqueViolationError(createAssetMutation.error) &&
    handleAPIError(createAssetMutation.error).apiStatusCode !==
      ERROR_CODE_MAPPING.asset_invalid_inventory_date &&
    !isPlanLimitError(createAssetMutation.error);

  /**
   * Closes the dialog and resets the form state.
   * Called on successful submission or user cancellation.
   */
  const close = () => {
    props.onClose();
    reset();
    setTakenSerialAsset(null);
  };

  /**
   * Handles form submission after validation.
   * Creates the asset with normalized data and handles success/error states.
   */
  const handleValidSubmission: SubmitHandler<OutputFormData> = (data) => {
    setTakenSerialAsset(null);
    createAssetMutation.mutate({
      inventoryDate: data.inventoryDate,
      itemId: data.item.id,
      serial: data.serial,
      locationId: data.location.id,
    });
  };

  // Read every render, so react-hook-form subscribes to the serial error.
  const hasSerialError = formState.errors.serial !== undefined;

  /**
   * Opens the location creation workflow in a new tab.
   * Called when a user needs to create a new location.
   */
  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);
    window.open(url, '_blank');
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while creating the asset."
        onClose={createAssetMutation.reset}
      />
      <DialogTitle>Add asset</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormItemSearch
            name="item"
            label="Item"
            control={control}
            filter={filterItems}
          />
          <Stack spacing={0.5}>
            <FormTextField name="serial" label="Serial" control={control} />
            {takenSerialAsset !== null && hasSerialError && (
              <TakenSerialLink existingAsset={takenSerialAsset} />
            )}
          </Stack>
          <FormLocationSearch
            name="location"
            label="Location"
            control={control}
            onCreate={handleCreateLocation}
          />
          <FormDatePicker
            disableFuture
            name="inventoryDate"
            label="Inventory date"
            control={control}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
